// src/services/auth/storage.ts

const AUTH_STORAGE_KEY = 'auth-storage';
const REFRESH_TOKEN_KEY = 'refresh-token';

export interface AuthTokens {
    access_token: string;
    refresh_token: string;
    expires_in?: number;
    token_type?: string;
}

export interface UserData {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    user_type: 'customer' | 'seller' | 'admin';
    status: string;
    profile_image?: string;
    phone?: string;
    created_at: string;
}

export interface AuthState {
    user: UserData | null;
    tokens: AuthTokens | null;
    isAuthenticated: boolean;
}

class AuthStorage {
    private getStorageData(): AuthState | null {
        try {
            const data = localStorage.getItem(AUTH_STORAGE_KEY);
            if (!data) return null;

            const parsed = JSON.parse(data);
            return parsed.state || null;
        } catch (error) {
            console.error('Error parsing auth storage:', error);
            return null;
        }
    }

    private setStorageData(state: AuthState): void {
        try {
            const data = {
                state,
                version: 1,
            };
            localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(data));
        } catch (error) {
            console.error('Error saving auth storage:', error);
        }
    }

    // Obtener token de acceso
    getAccessToken(): string | null {
        const data = this.getStorageData();
        return data?.tokens?.access_token || null;
    }

    // Obtener token de refresh
    getRefreshToken(): string | null {
        const data = this.getStorageData();
        return data?.tokens?.refresh_token || null;
    }

    // Obtener usuario actual
    getCurrentUser(): UserData | null {
        const data = this.getStorageData();
        return data?.user || null;
    }

    // Verificar si está autenticado
    isAuthenticated(): boolean {
        const data = this.getStorageData();
        return data?.isAuthenticated || false;
    }

    // Guardar tokens y usuario
    setAuthData(user: UserData, tokens: AuthTokens): void {
        const state: AuthState = {
            user,
            tokens,
            isAuthenticated: true,
        };
        this.setStorageData(state);
    }

    // Actualizar solo tokens (para refresh)
    setTokens(accessToken: string, refreshToken: string): void {
        const currentData = this.getStorageData();
        if (!currentData) return;

        const newTokens: AuthTokens = {
            access_token: accessToken,
            refresh_token: refreshToken,
        };

        const state: AuthState = {
            ...currentData,
            tokens: newTokens,
        };
        this.setStorageData(state);
    }

    // Actualizar datos del usuario
    updateUser(userData: Partial<UserData>): void {
        const currentData = this.getStorageData();
        if (!currentData?.user) return;

        const updatedUser = {
            ...currentData.user,
            ...userData,
        };

        const state: AuthState = {
            ...currentData,
            user: updatedUser,
        };
        this.setStorageData(state);
    }

    // Limpiar todos los datos de autenticación
    clearTokens(): void {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
    }

    // Verificar si el token está expirado (básico)
    isTokenExpired(): boolean {
        const token = this.getAccessToken();
        if (!token) return true;

        try {
            // Decodificar JWT básico (sin verificación)
            const payload = JSON.parse(atob(token.split('.')[1]));
            const currentTime = Math.floor(Date.now() / 1000);
            return payload.exp < currentTime;
        } catch (error) {
            return true;
        }
    }

    // Obtener información del token
    getTokenInfo(): { userId?: number; userType?: string; exp?: number } | null {
        const token = this.getAccessToken();
        if (!token) return null;

        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return {
                userId: payload.user_id,
                userType: payload.user_type,
                exp: payload.exp,
            };
        } catch (error) {
            return null;
        }
    }
}

// Instancia singleton
export const authStorage = new AuthStorage();